import { useCallback, useMemo } from 'react'

import _ from 'lodash'
import * as Params from 'use-query-params'

import { DataPackages } from '@piv/lib/rest-queries'

import { useInventoryFilterTab } from './useInventoryFilterTab'

const ArrayParam = Params.withDefault(Params.ArrayParam, [])

export type FilterParams = {
  [key in keyof DataPackages.Types.Filters]: typeof ArrayParam
}

export type NormalisedFilterParams = DataPackages.Types.Filters

export type FilterSetters = {
  [key in keyof DataPackages.Types.Filters]: (value: string[]) => void
}

const defaultFilters = {
  asset: [],
  subAsset: [],
  region: [],
  brand: [],
  currency: [],
  products: [],
  instruments: [],
}

export const useInventoryFilterParams = () => {
  const [filters, setFilters] = Params.useQueryParams<FilterParams>({
    asset: ArrayParam,
    subAsset: ArrayParam,
    region: ArrayParam,
    brand: ArrayParam,
    currency: ArrayParam,
    products: ArrayParam,
    instruments: ArrayParam,
  })
  const [queryString, setQueryString] = Params.useQueryParam(
    'q',
    Params.StringParam
  )
  const { filterTab } = useInventoryFilterTab()
  const isOnFilterTab = filterTab === 'filters'

  const safeFilters = useMemo<NormalisedFilterParams>(() => {
    return _.mapValues(filters, (value) => {
      return value.filter(
        (item) => typeof item === 'string' && item.length > 0
      ) as string[]
    })
  }, [filters])

  const filterSetters = useMemo<FilterSetters>(() => {
    return _.mapValues(filters, (_, key) => (value: string[]) => {
      setFilters({ [key]: value })
      return
    })
  }, [filters, setFilters])

  const resetFilters = useCallback(() => {
    setFilters({ ...defaultFilters })
    setQueryString(undefined)
  }, [setFilters, setQueryString])

  const validFilterCount = useMemo(
    () =>
      Object.values(safeFilters).reduce((count, filter) => {
        count += filter.length

        return count
      }, 0),
    [safeFilters]
  )

  return {
    filters: isOnFilterTab ? safeFilters : { ...defaultFilters },
    filterSetters,
    setFilters,
    resetFilters,
    isAnyFilterSet: validFilterCount > 0,
    validFilterCount,
    queryString: isOnFilterTab ? '' : queryString ?? '',
    setQueryString,
  }
}
